import polyline from 'google-polyline';
import { compact, uniqWith } from 'lodash';

import {
  AIRPORT,
  CANAL_TRANSIT,
  CAPE_OF_GOOD_HOPE_ID,
  MAP_PATH_STYLE,
  PANAMA_CANAL_ID,
  SEAPORT,
  SUEZ_CANAL_ID,
  TRANSFER_TRANSFER,
  VEHICLE,
  WAYPOINT,
} from '@/lib/constants';
import { LocationModel, MinimalLocationModel } from '@/lib/models/location/types';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { VesselLocationModel } from '@/lib/models/vessel-location/types';
import { MapMarker, MapPath, MapPathStyle, MarkerSize, Paths, PositionInterface } from '@/types';
import { NodeType, PlaceType, PortApiInterface, PortNodeApiInterface, RouteTransferApiType } from '@/types/api-types';
import { formatDateForUser } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';

import { formatDateTimeForUser } from '../format/date-time';

export const createPathId = (a: string, b: string, prefix: string | number = 'P'): string => {
  return `${prefix}_${a}_${b}`;
};

/**
 * Decodes a path encoded in the Encoded Polyline Algorithm Format
 */
export const decodePolylinePath = (encodedPath?: string): PositionInterface[] | null => {
  if (encodedPath) {
    const decodedPath = polyline.decode(encodedPath);
    const objectPath = decodedPath.map((pos) => {
      const [lat, lng] = pos;
      return {
        lat,
        lng,
      };
    });
    return objectPath;
  }

  return null;
};

/**
 * Creates a Straight Line from a list of node Ids
 * Used for Air results or when Ocean paths fail
 */
export const createPathsFromLocations = (
  locations: LocationModel[] | MapMarker[] | MinimalLocationModel[],
  pathHash?: number | string,
): Paths[] => {
  if (!locations) return [];

  return locations.reduce((pathAccum, location, index) => {
    const nextLocation = locations[index + 1];
    if (nextLocation) {
      const path: Paths = {
        id: createPathId(location.id, nextLocation.id, pathHash),
        fromId: location.id,
        toId: nextLocation.id,
        positions: [location.location, nextLocation.location],
      };
      return [...pathAccum, path];
    }
    return pathAccum;
  }, [] as Paths[]);
};

/**
 * Gets a specific Map marker depending on the Type and TransferType
 */
export const getMapMarkerIcon = (type: NodeType | PlaceType, transferType?: RouteTransferApiType | null): string => {
  switch (type) {
    case AIRPORT:
      return 'plane';
    case SEAPORT:
      // Switch with in a switch
      switch (transferType) {
        case TRANSFER_TRANSFER:
          return 'split';
        case CANAL_TRANSIT:
          return 'waves';
        default:
          return 'anchor';
      }
    default:
      return '';
  }
};

/**
 * Converts raw decoded paths into a MapPath so we can pass it to the Map
 */
export const pathsToMapPaths = (rawPaths: Paths[] | undefined, style: MapPathStyle = {}): MapPath[] => {
  if (!rawPaths) return [];
  return rawPaths.map((path) => {
    return {
      ...path,
      style: {
        ...MAP_PATH_STYLE,
        ...style,
      },
    };
  });
};

export const createMarkersFromPorts = (
  ports: (PortApiInterface | PortNodeApiInterface)[],
  completedPorts: string[],
  shouldShowStartEndLabels = true,
): MapMarker[] => {
  const markers = ports.map((port, index) => {
    if (!port.location) return null;

    const isFirst = index === 0;
    const isLast = index === ports.length - 1;
    const isFirstOrLast = isFirst || isLast;
    const isAirportOrSeaport = port.type === AIRPORT || port.type === SEAPORT;
    const identifiers = (port as PortNodeApiInterface).identifiers || (port as PortApiInterface).ids || [];
    const identifier = getPreferredIdentifier(identifiers);
    const label = isAirportOrSeaport ? identifier?.value : port.name;
    const isCompleted = identifier?.value ? completedPorts.includes(identifier?.value) : false;
    const shouldHighlightFirstOrLast = isFirstOrLast && shouldShowStartEndLabels;
    const markerColor = 'white';
    const size = shouldHighlightFirstOrLast ? 'xl' : 'lg';
    const icon = isCompleted ? 'milestone-complete' : 'milestone-pending';
    const isHidden = port.type === WAYPOINT;

    return {
      id: `${identifier?.type} ${identifier?.value}`,
      type: 'tracking' as MapMarker['type'],
      color: markerColor,
      location: port.location,
      label: label || '',
      size: size as MapMarker['size'],
      active: false,
      hasStaticLabel: shouldHighlightFirstOrLast,
      hidden: isHidden,
      icon,
      nodeType: port.type as MapMarker['nodeType'],
      zIndex: isFirstOrLast ? ports.length + 2 : index + 1,
    };
  });

  return compact(markers);
};

export const createMarkersFromVehicleLocations = (locations: VesselLocationModel[], isActive: boolean): MapMarker[] => {
  const sortedLocations = locations.sort((a, b) => {
    return new Date(a.pingedAt).getTime() - new Date(b.pingedAt).getTime();
  });

  const dedupedLocations = uniqWith(
    sortedLocations,
    (locationA, locationB) => locationA.longitude === locationB.longitude && locationA.latitude === locationB.latitude,
  );

  return dedupedLocations.map((location, index) => {
    const isActiveMarker = isActive && index === dedupedLocations.length - 1;

    return {
      id: location.id,
      type: 'tracking',
      color: isActiveMarker ? '' : 'lightBlue.400',
      location: { lat: location.latitude, lng: location.longitude },
      label: isActiveMarker ? 'Current location' : formatDateForUser(location.pingedAt),
      size: (isActiveMarker ? '2xl' : 'sm') as MarkerSize,
      active: isActiveMarker,
      icon: isActiveMarker ? 'vehicle-location' : '',
      nodeType: VEHICLE,
      zIndex: 1,
    };
  });
};

export const createMarkerFromShipmentModel = (shipment: ShipmentModel, isActive: boolean): MapMarker | null => {
  if (shipment.lastLocation) {
    const eta = shipment.eta ? formatDateTimeForUser(new Date(shipment.eta)) : null;

    return {
      id: `shipment-location:${shipment.id}`,
      type: 'tracking' as const,
      color: '',
      location: { lat: shipment.lastLocation?.latitude, lng: shipment.lastLocation?.longitude },
      bearing: shipment.lastLocation?.bearing || 0,
      label: `${shipment.containerNumber || shipment.awbNumber || shipment.mblNumber || shipment.bookingNumber || ''}${eta ? ` - ETA: ${eta}` : ''}`,
      size: isActive ? '3xl' : '2xl',
      active: isActive,
      icon: 'vehicle-location',
      nodeType: VEHICLE,
      zIndex: 1,
    };
  }

  return null;
};

export const createPathFromVehicleLocations = (locations: VesselLocationModel[]): Paths[] => {
  if (!locations) return [];

  // @TODO consider encoding path for smaller payload size
  return locations.reduce((pathAccum, location, index) => {
    const nextLocation = locations[index + 1];
    if (nextLocation) {
      const locationA = { lat: location.latitude, lng: location.longitude };
      const locationB = { lat: nextLocation.latitude, lng: nextLocation.longitude };
      const path: Paths = {
        id: createPathId(location.id, nextLocation.id),
        fromId: location.id,
        toId: nextLocation.id,
        positions: [locationA, locationB],
      };
      return [...pathAccum, path];
    }
    return pathAccum;
  }, [] as Paths[]);
};

/**
 * Maps MarkerSize values to Tailwind size classes for dynamic icons
 */
export const getMarkerSizeClass = (size: MarkerSize): string => {
  const sizeMap: Record<MarkerSize, string> = {
    sm: '!size-3',
    md: '!size-4',
    lg: '!size-5',
    xl: '!size-6',
    '2xl': '!size-7',
    '3xl': '!size-8',
  };

  return sizeMap[size] || '!size-5'; // Default to lg size if not found
};

// Merge paths that are connected through waypoints
// this is specific required for vessels that pass through hubs like the Cape of Good Hope,
// Panama Canal, or Suez Canal that don't have an event timestamp (therefore no start/end time for estimating journey progress)
export const mergePathsOnWaypoints = (paths: Paths[]): Paths[] => {
  const hubLocodes = new Set([CAPE_OF_GOOD_HOPE_ID, PANAMA_CANAL_ID, SUEZ_CANAL_ID]);
  const result: Paths[] = [];

  let i = 0;
  while (i < paths.length) {
    let currentPath = paths[i];

    // Check if we can merge with subsequent paths
    while (i + 1 < paths.length && hubLocodes.has(currentPath.toId) && currentPath.toId === paths[i + 1].fromId) {
      const nextPath = paths[i + 1];

      // Create merged path
      currentPath = {
        id: `${currentPath.id}_${nextPath.id}`,
        fromId: currentPath.fromId,
        toId: nextPath.toId,
        positions: [...currentPath.positions, ...nextPath.positions],
      };

      i += 1; // Skip the next path since we merged it
    }

    result.push(currentPath);
    i += 1;
  }

  return result;
};
